import { largeMode, smallMode, close } from './resources/Icons.js';
import './Minimap3DMobile.css'; // IMPORTANT!!

const av = Autodesk.Viewing;
const avu = av.UI;
const GlobalManagerMixin = av.GlobalManagerMixin;

const namespace = AutodeskNamespace('Autodesk.AEC');

const PaddingFromEdge = 16; // Should match the toolbar padding, in pixels.
const DefaultToolbarHeight = 308; // Height of the vertical toolbar (mobile app) in pixels.

// Params for choosing mapWidget size based on canvas size - relevant for Tablets only.
const MinimapWidthToHeightRatio = 0.75;
const RelMapWidgetSizeLongEdge  = 0.35; // Restrict to max 25% of the canvas along the longer axis with
const RelMapWidgetSizeShortEdge = 0.45; // Restrict to max 37% of the canvas along the shorter axis (more tolerance here, to keep map useful for widescreen)

// int value to string, e.g. 50 => "50px" - to used for style assignments.
const toPixels = (val) => {
    return val.toString() + 'px';
};

export default class Minimap3DMobileLayout {
    constructor(minimap3DExtension) {
        this.minimap3DExtension = minimap3DExtension;
        this.viewer = this.minimap3DExtension.viewer;
        this.options = minimap3DExtension.options;

        this.setGlobalManager(this.viewer.globalManager);

        this.update = this.update.bind(this);
        this.onResize = this.onResize.bind(this);

        this.isLargeMode = this.options.isLargeMode || false;

        this.toolbarHeight = this.options.toolbarHeight || DefaultToolbarHeight;

        this.isTablet = this.options.isTablet || false; // Tablet / Phone.

        this.init();
    }

    initWidgets() {
        const _document = this.getDocument();

        // Optional: add extra css class
        const classPostfix = this.options.additionalWrapperCss ? ' ' + this.options.additionalWrapperCss : '';

        this.mapWidget = _document.createElement('div');
        this.mapWidget.className = `minimap3D-mobile ${classPostfix} `;
        this.mapWidget.classList.add('adsk-viewing-viewer');
        this.mapWidget.classList.add(this.viewer.theme);
        this.viewer.clientContainer.appendChild(this.mapWidget);

        this.buttonsContainer = _document.createElement('div');
        this.buttonsContainer.className = `buttons-container`;
        this.mapWidget.appendChild(this.buttonsContainer);

        const ButtonColor = "#0696D7";

        // Resize button
        this.resizeButton = new avu.Button("minimap_mobile_resize");
        this.resizeButton.setGlobalManager(this.viewer.globalManager);
        this.resizeButton.container.style.pointerEvents = 'auto';
        this.resizeButton.icon.style.width = "20px";
        this.resizeButton.icon.style.height = "20px";
        this.resizeButton.icon.style.paddingLeft = "5px";
        this.resizeButton.icon.style.paddingTop = "3px";
        this.resizeButton.icon.style.color = ButtonColor;
        this.resizeButton.onClick = () => this.minimap3DExtension.setSideBySideEnabled(!this.isLargeMode);
        this.buttonsContainer.appendChild(this.resizeButton.container);

        // Vertical divider between the buttons
        this.divider = _document.createElement('div');
        this.divider.className = `divider`;
        this.buttonsContainer.appendChild(this.divider);

        // Close button
        this.closeButton = new avu.Button("minimap_mobile_close");
        this.closeButton.setGlobalManager(this.viewer.globalManager);
        this.closeButton.container.style.pointerEvents = 'auto';
        this.closeButton.icon.innerHTML = close;
        this.closeButton.icon.style.width = "20px";
        this.closeButton.icon.style.height = "20px";
        this.closeButton.icon.style.paddingLeft = "7px";
        this.closeButton.icon.style.paddingTop = "2px";
        this.closeButton.icon.style.color = ButtonColor;
        this.closeButton.onClick = () => this.minimap3DExtension.changeMinimapVisibility(false);
        this.buttonsContainer.appendChild(this.closeButton.container);

        // Call setSideBySideEnabled directly at initialization, in order to set the initial state of the button.
        this.setSideBySideEnabled(this.isLargeMode);

        this.changeDeviceType(this.isTablet);
        this.update(true);
    }

    destroyWidgets() {
        if (this.mapWidget.parentElement) {
            this.mapWidget.parentElement.removeChild(this.mapWidget);
        }

        this.mapWidget = null;
        this.resizeButton = null;
        this.closeButton = null;
        this.divider = null;
    }

    init() {
        this.initWidgets();

        this.viewer.addEventListener(av.VIEWER_RESIZE_EVENT, this.onResize);
    }

    destroy() {
        this.destroyWidgets();

        this.viewer.removeEventListener(av.VIEWER_RESIZE_EVENT, this.onResize);
    }

    setMinimapVisible(visible) {
        if (this.visible === visible) {
            return;
        }

        this.visible = visible;

        // While switching sheets, mapWidget might be null
        if (!this.mapWidget) {
            return;
        }

        if (visible) {
            this.mapWidget.classList.add('visible');
        } else {
            this.mapWidget.classList.remove('visible');
        }
    }

    getMapViewerContainer() {
        return this.mapWidget;
    }

    update(immediate) {
        const visible = this.minimap3DExtension.isEnabled();
        this.setMinimapVisible(visible);

        if (!visible) {
            return;
        }

        this.updateWidgetClasses();
        this.updateToolbar();
        return this.updateWidgetSize(immediate);
    }

    updateToolbar() {
        this.sideBySideButton && this.sideBySideButton.update();
        this.expandButton && this.expandButton.update();
    }

    isPortrait() {
        const rect = this.viewer.impl.getCanvasBoundingClientRect();
        const isPortrait = rect.width < rect.height;

        return isPortrait;
    }

    updateWidgetClasses() {
        const isPortrait = this.isPortrait();

        // Update css classes
        if (isPortrait) {
            !this.mapWidget.classList.contains('portrait') && this.mapWidget.classList.add('portrait');
        } else {
            this.mapWidget.classList.contains('portrait') && this.mapWidget.classList.remove('portrait');
        }

        if (this.isTablet) {
            this.mapWidget.classList.contains('phone') && this.mapWidget.classList.remove('phone');
        } else {
            !this.mapWidget.classList.contains('phone') && this.mapWidget.classList.add('phone');
        }
    }

    // Update widget size according to:
    // - Tablet / Phone
    // - LargeMode / SmallMode
    // - Portrait / Landscape
    updateWidgetSize(immediate) {
        const rect = this.viewer.impl.getCanvasBoundingClientRect();
        const isPortrait = this.isPortrait();

        let targetWidth;
        let targetHeight;

        if (this.isTablet) {
            if (this.isLargeMode) {
                if (isPortrait) {
                    // Tablet-LargeMode-Portrait:   Minimap covers up to half of the screen, from the top.
                    //                              In case of relatively short tablet, make sure that it won't reach until the vertical toolbar.
                    targetWidth = rect.width - (2 * PaddingFromEdge);
                    const maxHeight = Math.min(rect.height - this.toolbarHeight, rect.height / 2) - (2 * PaddingFromEdge);
                    targetHeight = maxHeight - (2 * PaddingFromEdge);
                } else {
                    // Tablet-LargeMode-Landscape:  Minimap covers half of the screen, from the left.
                    targetWidth = (rect.width / 2) - (2 * PaddingFromEdge);
                    targetHeight = rect.height - (2 * PaddingFromEdge);
                }
            } else {
                // Tablet-SmallMode (both Portrait & Landscape): Minimap is a small rectangle at the top left corner of the screen.
                //                                               Size of the minimap is relative to the screen size.
                const longEdge = Math.max(rect.width, rect.height);
                const shortEdge = Math.min(rect.width, rect.height);

                // Restrict mapSize to not exceed a certain percentage of canvas width/height
                targetWidth = Math.min(RelMapWidgetSizeLongEdge * longEdge, RelMapWidgetSizeShortEdge * shortEdge);
                targetHeight = targetWidth * MinimapWidthToHeightRatio;
            }
        } else {
            if (this.isLargeMode) {
                if (isPortrait) {
                    // Phone-LargeMode-Portrait:    Minimap covers almost the entire screen, up until a few pixels at the bottom.
                    targetWidth = rect.width;
                    targetHeight = rect.height - PaddingFromEdge;
                } else {
                    // Phone-LargeMode-Landscape:   Minimap covers almost the entire screen, up until a few pixels at the right.
                    targetWidth = rect.width - PaddingFromEdge;
                    targetHeight = rect.height;
                }
            } else {
                if (isPortrait) {
                    // Phone-SmallMode-Portrait:    Minimap covers less than half of the screen, from the top.
                    //                              In case of relatively short phone, make sure that it won't reach until the vertical toolbar.
                    targetWidth = rect.width;
                    const maxHeight = Math.min(rect.height - this.toolbarHeight, rect.height / 2.5) - (2 * PaddingFromEdge);
                    targetHeight = Math.min(rect.width, maxHeight);
                } else {
                    // Phone-SmallMode-Landscape:   Minimap covers less than half of the screen, from the left.
                    targetWidth = rect.width / 2.5;
                    targetHeight = rect.height;
                }
            }
        }

        targetWidth = Math.floor(targetWidth);
        targetHeight = Math.floor(targetHeight);

        return this.animateWidgetSize(targetWidth, targetHeight, immediate);
    }

    animateWidgetSize(targetWidth, targetHeight, immediate) {
        if (targetWidth === this.mapWidgetWidth && targetHeight === this.mapWidgetHeight) {
            return Promise.resolve();
        }

        const startWidth = this.mapWidgetWidth || targetWidth;
        const startHeight = this.mapWidgetHeight || targetHeight;

        if (this.widgetAnimation) {
            this.widgetAnimation.stop();
            this.widgetAnimation = null;
        }

        if (immediate) {
            this.setWidgetSize(targetWidth, targetHeight);
            return Promise.resolve();
        }

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);

            const width = Autodesk.Viewing.Private.lerp(startWidth, targetWidth, t);
            const height = Autodesk.Viewing.Private.lerp(startHeight, targetHeight, t);

            this.setWidgetSize(width, height);
        };

        return new Promise(resolve => {
            const onAnimEnd = () => {
                this.widgetAnimation = null;
                resolve();
            };

            this.widgetAnimation = Autodesk.Viewing.Private.fadeValue(0, 1, this.minimap3DExtension.resizeDuration, onTimer, onAnimEnd);
        });
    }

    setWidgetSize(width, height) {
        this.mapWidget.style.width = toPixels(width);
        this.mapWidget.style.height = toPixels(height);

        this.mapWidgetWidth = width;
        this.mapWidgetHeight = height;

        this.minimap3DExtension.mapViewer?.resize();
    }

    setSideBySideEnabled(isLargeMode) {
        this.isLargeMode = isLargeMode;

        this.resizeButton.icon.innerHTML = this.isLargeMode ? smallMode : largeMode;

        this.minimap3DExtension.fireEvent({ type: namespace.Minimap3DExtension.Events.LARGE_MODE_CHANGED, isLargeMode });

        this.update();
    }

    changeDeviceType(isTablet) {
        this.isTablet = isTablet;

        this.mapWidget.style.margin = this.isTablet ? toPixels(PaddingFromEdge) : 'initial';

        // Don't show resize button for phones.
        this.resizeButton.container.style.display = this.isTablet ? 'block' : 'none';
        this.divider.style.display = this.isTablet ? 'block' : 'none';

        this.update(true);
    }

    setToolbarHeight(toolbarHeight) {
        this.toolbarHeight = toolbarHeight;

        this.update(true);
    }

    onResize() {
        this.update(true);
    }
}

GlobalManagerMixin.call(Minimap3DMobileLayout.prototype);
